import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Loader2, Ticket, CalendarDays } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const sel = "eyw-input !py-2 text-sm";

// Front-desk package redemption: pick an eligible service, slot and enhancer,
// then book it against the member's package (deducts a session, records a
// package-redemption sale, syncs calendars and sends the confirmation email).
export default function RedeemPackageModal({ credit, clientId, onClose, onDone }) {
  const qc = useQueryClient();
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [enhancerId, setEnhancerId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { data: elig } = useQuery({
    queryKey: ["pos-credit-eligible", credit.id],
    queryFn: () => api.get(`/pos/credits/${credit.id}/eligible-services`).then((r) => r.data),
  });
  const services = elig?.services || [];
  const svc = services.find((s) => s.id === (serviceId || services[0]?.id));
  const effServiceId = serviceId || services[0]?.id || "";

  const { data: slotData, isFetching: slotsLoading } = useQuery({
    queryKey: ["pos-redeem-slots", effServiceId, date],
    queryFn: () => api.get(`/services/${effServiceId}/open-slots`, { params: { date } }).then((r) => r.data),
    enabled: !!effServiceId && !!date,
  });
  const slots = slotData?.slots || [];

  const { data: assignable = [] } = useQuery({
    queryKey: ["pos-assignable"],
    queryFn: () => api.get("/pos/assignable").then((r) => r.data),
  });
  const enhancers = (Array.isArray(assignable) ? assignable : assignable.people || []).filter((p) => (p.type || p.role || "enhancer") !== "vendor");

  const redeem = async () => {
    setError(""); setSaving(true);
    try {
      const { data } = await api.post("/pos/package-redemption", {
        client_id: clientId, credit_id: credit.id, service_id: effServiceId,
        date, start_time: time, enhancer_id: enhancerId,
      });
      toast.success(`Session redeemed — ${data.package_name}: ${data.sessions_remaining} session${data.sessions_remaining === 1 ? "" : "s"} left.`);
      qc.invalidateQueries({ queryKey: ["pos-client", clientId] });
      qc.invalidateQueries({ queryKey: ["pos-sales"] });
      onDone?.(data);
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError((d && (d.message || formatApiError(d))) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[140] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="redeem-package-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-7 max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} data-testid="redeem-close" className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Ticket size={18} className="text-[#9E5C7F]" /> Redeem package session</h3>
        <p className="text-sm text-[#4A5D6B] mt-1" data-testid="redeem-credit-info">
          {credit.package_name} · <b className="text-[#5C8A72]">{credit.sessions_remaining}/{credit.sessions_total}</b> left
          {credit.expires_at ? ` · expires ${String(credit.expires_at).slice(0, 10)}` : ""}
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-[#1A242B] block mb-1.5">Service</label>
            <select value={effServiceId} onChange={(e) => { setServiceId(e.target.value); setTime(""); }} data-testid="redeem-service-select" className={`${sel} w-full`}>
              {services.map((s) => <option key={s.id} value={s.id}>{s.title} ({s.duration_min} min)</option>)}
              {services.length === 0 && <option value="">Loading…</option>}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#1A242B] block mb-1.5">Date</label>
              <input type="date" value={date} min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => { setDate(e.target.value); setTime(""); }} data-testid="redeem-date" className={`${sel} w-full`} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A242B] block mb-1.5">Time</label>
              {slotsLoading ? <Loader2 size={16} className="animate-spin text-[#144F75] mt-2.5" /> : (
                <select value={time} onChange={(e) => setTime(e.target.value)} disabled={!slots.length} data-testid="redeem-time" className={`${sel} w-full disabled:opacity-50`}>
                  <option value="">{date ? (slots.length ? "Pick a time" : "No open times") : "Pick a date first"}</option>
                  {slots.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#1A242B] block mb-1.5">Enhancer <span className="text-[#8FA89B] font-normal">(optional — defaults to the service's enhancer)</span></label>
            <select value={enhancerId} onChange={(e) => setEnhancerId(e.target.value)} data-testid="redeem-enhancer" className={`${sel} w-full`}>
              <option value="">Service default</option>
              {enhancers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mt-3" data-testid="redeem-error">{error}</p>}
        <button onClick={redeem} disabled={saving || !effServiceId || !date || !time} data-testid="redeem-submit" className="eyw-btn w-full mt-5 disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <CalendarDays size={17} />}
          Redeem 1 session & book
        </button>
        <p className="text-xs text-[#8FA89B] mt-2.5 text-center">Deducts one session, books the appointment and sends the member their confirmation.</p>
      </div>
    </div>
  );
}
